import React from "react";
import { FontStyles } from "../utils/font-styles.ts";
import { SectionHeader } from "../components/section-header.tsx";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";

const StatCard: React.FC<{ icon: string; title: string; statistic: string }> = ({ icon, title, statistic }) => {
  return (
    <div className={"flex py-3.5 px-3 lg:px-5 bg-loch-50 border border-loch rounded-md gap-3 md:gap-3.5"}>
      <img
        className={"w-[38px] md:w-[48px] lg:w-[52px] h-[38px] md:h-[48px] lg:h-[52px] my-auto"}
        alt={`${title} icon`}
        src={icon}
      />
      <div className={"flex flex-col gap-1.5 font-semibold justify-center"}>
        <div className={`text-north-sea ${FontStyles["body-md"]} !leading-none`}>{title}</div>
        <div className={`text-loch ${FontStyles.h6} !leading-none`}>{statistic}</div>
      </div>
    </div>
  );
};

export const DistrictStats: React.FC = () => {
  const {
    districtStatsLocation: location = "",
    averageSellingPrice = 0,
    medianDaysToSell = 0,
    averagePercentOfHomeReportAchieved,
    mostPopularPropertyType = "",
    isUsingParentAreaStats = false,
    parentAreaName,
    districtStatsEndOfPeriod: endOfPeriod = "",
    districtStatsLengthOfPeriodMonths: lengthOfPeriodMonths = "",
  } = usePropertyDetailsContext();
  const tiles = [
    {
      title: "Average selling price",
      statistic: `£${averageSellingPrice.toLocaleString("en-GB")}`,
      icon: "/static/icons/AverageSellingPrice.svg",
    },
    {
      title: "Median time to sell",
      statistic: `${medianDaysToSell.toString()} ${medianDaysToSell > 1 ? "days" : "day"}`,
      icon: "/static/icons/MedianTimeToSell.svg",
    },
    {
      title: "Most popular property type",
      statistic: mostPopularPropertyType,
      icon: "/static/icons/MostPopularPropertyType.svg",
    },
  ];
  if (averagePercentOfHomeReportAchieved != null) {
    tiles.splice(2, 0, {
      title: "Average % of Home Report achieved",
      icon: "/static/icons/HRAchieved.svg",
      statistic: `${averagePercentOfHomeReportAchieved.toString()}%`,
    });
  }
  return (
    <div className={"flex flex-col gap-6 w-full"}>
      <div className={"flex flex-col gap-3 lg:gap-4.5"}>
        <SectionHeader text={`${location} at a glance*`} as={"h2"} className={FontStyles.h5} />
        <div className={"flex flex-col md:grid md:grid-cols-2 gap-4 lg:gap-4.5"}>
          {tiles.map((stat, index) => {
            return <StatCard key={index} title={stat.title} statistic={stat.statistic} icon={stat.icon} />;
          })}
        </div>
      </div>
      <div className={`${FontStyles["body-s"]} text-slate`}>
        *
        {isUsingParentAreaStats && parentAreaName != null
          ? " Sales statistics shown here are for the wider " + parentAreaName + " area, due to data availability. "
          : " "}
        Figures are based on properties marketed and sold through ESPC. Home Report valuations relate to properties
        where the Home Report was available on espc.com. Figures relate to the {lengthOfPeriodMonths} month period
        ending on {endOfPeriod}.
      </div>
    </div>
  );
};
